import React, { useRef } from 'react';
import { Overlay } from './Overlay';
import { BriefSectionView } from './BriefSectionView';
import { ContentsNav } from './ContentsNav';
import { ConceptUsage } from './ConceptUsage';
import { ConceptPdf } from './pdf/ConceptPdf';
import { useActiveSection } from '../../hooks/useActiveSection';
import { briefClosing, briefSections, briefSubtitle } from '../../data/brief';
import type { Format } from '../../types/snak';
interface ConceptOverlayProps {
  format: Format;
  onFormatChange: (format: Format) => void;
  onClose: () => void;
}
export function ConceptOverlay({
  format,
  onFormatChange,
  onClose
}: ConceptOverlayProps) {
  const compact = format === 'mobile';
  const bodyRef = useRef<HTMLDivElement>(null);
  const active = useActiveSection(briefSections.map(s => s.id), bodyRef, !compact);
  const goTo = (id: string) => {
    const element = document.getElementById(id);
    if (element) element.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };
  return <Overlay eyebrow="Concept & strategy" format={format} onFormatChange={onFormatChange} onClose={onClose} bodyRef={bodyRef} download={{
    fileName: 'Meet-the-Filteks-Concept-and-Strategy.pdf',
    orientation: 'portrait',
    render: () => <ConceptPdf data-id="element-540" data-mp="1ps3e4i8kcgg2" />
  }} data-id="element-539" data-mp="1l13irhf3e6fo">
      <div className={compact ? 'px-6 pb-14 pt-10' : 'mx-auto flex max-w-[1160px] items-start gap-12 px-8 pb-24 pt-14 lg:gap-16'} data-id="element-541" data-mp="1pt519l2uste5">
        {!compact && <ContentsNav variant="rail" active={active} onSelect={goTo} className="hidden lg:block" data-id="element-542" data-mp="y3iccgtxmgjs" />}

        <div className={compact ? '' : 'min-w-0 max-w-[700px] flex-1'} data-id="element-543" data-mp="1712io46nj319">
          <h1 className={['font-heading leading-[0.96] tracking-[-0.03em] text-background', compact ? 'text-[34px]' : 'text-[46px] sm:text-[56px]'].join(' ')} data-id="element-544" data-mp="2nfu7y88i3u6c">
            Meet the Filteks
          </h1>
          <p className={['max-w-[28ch] font-heading leading-[1.25] tracking-[-0.01em] text-[rgba(1,51,43,0.6)]', compact ? 'mt-4 text-[16px]' : 'mt-5 text-[21px]'].join(' ')} data-id="element-545" data-mp="2qkb40ewn9iix">
            {briefSubtitle}
          </p>

          {compact && <ContentsNav variant="index" active={active} onSelect={goTo} className="mt-8" data-id="element-546" data-mp="26tzgr6f3ct7u" />}

          <div className={compact ? 'mt-12 space-y-12' : 'mt-16 space-y-16'} data-id="element-547" data-mp="2btc3lr5yq0af">
            {briefSections.map((section, i) => <BriefSectionView key={section.id} section={section} index={i} compact={compact} data-id="element-548" data-mp="2lils6x1ll0ts" data-mpi={i} />)}
          </div>

          <section className={['border-t border-[rgba(1,51,43,0.14)]', compact ? 'mt-12 pt-8' : 'mt-16 pt-10'].join(' ')} data-id="element-549" data-mp="1t2b4920xk2qc">
            <p className={['max-w-[24ch] font-heading leading-[1.06] tracking-[-0.02em] text-background', compact ? 'text-[25px]' : 'text-[34px]'].join(' ')} data-id="element-550" data-mp="1gjyxmaior23v">
              {briefClosing.statement}
            </p>
            <p className={['mt-4 font-heading leading-tight text-[rgba(1,51,43,0.55)]', compact ? 'text-[17px]' : 'text-[22px]'].join(' ')} data-id="element-551" data-mp="16o5h6g1uiogt">
              {briefClosing.support}
            </p>
          </section>

          <footer className={['border-t border-[rgba(1,51,43,0.12)]', compact ? 'mt-12 pt-6' : 'mt-16 pt-8'].join(' ')} data-id="element-552" data-mp="ss9xw93jbbcw">
            <p className="font-body text-[12px] leading-relaxed text-[rgba(1,51,43,0.55)]" data-id="element-553" data-mp="ivf76cdi6f96">
              Original concept &amp; creative direction by Snakker™
            </p>
            <ConceptUsage data-id="element-554" data-mp="13mkb6jg0w0og" />
          </footer>
        </div>
      </div>
    </Overlay>;
}